import { useState, useEffect, type ReactNode } from 'react'
import { getContent } from '@/lib/api'
import { usePreview } from '@/hooks'
import type { ContentWithMeta } from '@/types'

interface EditableSectionProps {
  sectionKey: string
  field: string
  fallback?: string
  isHtml?: boolean
  render?: (value: string) => ReactNode
  className?: string
}

export function EditableSection({
  sectionKey,
  field,
  fallback = '',
  isHtml = false,
  render,
  className,
}: EditableSectionProps) {
  const [content, setContent] = useState<ContentWithMeta | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { isPreview, previewContent } = usePreview(sectionKey)

  // Fetch content on mount
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await getContent(sectionKey)
        setContent(response.content)
      } catch {
        // Content not found, use fallback
        setContent(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchContent()
  }, [sectionKey])

  // Get the value to display
  const getDisplayValue = (): string => {
    // In preview mode, use preview content if available
    if (isPreview && previewContent && field in previewContent) {
      return String(previewContent[field] || '')
    }

    // Otherwise use fetched content
    if (content && field in content) {
      return String(content[field] || '')
    }

    return fallback
  }

  const value = getDisplayValue()

  // Loading state - show fallback
  if (isLoading) {
    if (render) {
      return <>{render(fallback)}</>
    }
    return <span className={className}>{fallback}</span>
  }

  // Custom render function
  if (render) {
    return <>{render(value)}</>
  }

  // HTML content
  if (isHtml) {
    return (
      <span
        className={className}
        dangerouslySetInnerHTML={{ __html: value || fallback }}
      />
    )
  }

  // Plain text
  return <span className={className}>{value || fallback}</span>
}

// Wrapper component for sections that need the full content object
interface EditableSectionBlockProps {
  sectionKey: string
  children: (content: Record<string, unknown>, isPreview: boolean) => ReactNode
  fallback?: ReactNode
}

export function EditableSectionBlock({
  sectionKey,
  children,
  fallback = null,
}: EditableSectionBlockProps) {
  const [content, setContent] = useState<ContentWithMeta | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { isPreview, previewContent } = usePreview(sectionKey)

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await getContent(sectionKey)
        setContent(response.content)
      } catch {
        setContent(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchContent()
  }, [sectionKey])

  if (isLoading) {
    return <>{fallback}</>
  }

  const displayContent = isPreview && previewContent ? previewContent : content

  if (!displayContent) {
    return <>{fallback}</>
  }

  return <>{children(displayContent, isPreview)}</>
}
